import { useEffect, useState } from "react";
import { Check, Minus, ArrowUpRight } from "lucide-react";
import { APARTMENTS } from "@/data/apartments";
import { AvailabilityBadge } from "./AvailabilityBadge";
import { ApartmentDetailDialog } from "./ApartmentDetailDialog";
import api from "@/lib/api";
import { useT } from "@/i18n/LanguageProvider";

/**
 * Side-by-side comparison of all 4 apartments.
 * On mobile becomes a horizontally scrollable table.
 */
export const ApartmentsCompare = () => {
    const t = useT();
    const [statusMap, setStatusMap] = useState({});
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        api.get("/apartments")
            .then((r) => {
                const map = {};
                (r.data || []).forEach((a) => { map[a.id] = a.status; });
                setStatusMap(map);
            })
            .catch(() => {});
    }, []);

    const fmtPrice = (n) => `${n.toLocaleString("de-DE")} €`;

    const rows = [
        {
            label: t({ de: "Status", en: "Status" }),
            render: (a) => <AvailabilityBadge status={statusMap[a.id] || "verfuegbar"} size="sm" />,
        },
        {
            label: t({ de: "Lage", en: "Location" }),
            render: (a) => <span className="font-body text-sm text-stone-600">{a.position}</span>,
        },
        {
            label: t({ de: "Zimmer", en: "Rooms" }),
            render: (a) => <span className="font-heading text-2xl text-ink">{a.rooms}</span>,
        },
        {
            label: t({ de: "Wohnfläche", en: "Living area" }),
            render: (a) => (
                <span className="font-heading text-xl text-ink">
                    {a.size}<span className="text-sm text-stone-400 ml-1">{a.sizeUnit}</span>
                </span>
            ),
        },
        {
            label: t({ de: "Außenbereich", en: "Outdoor" }),
            render: (a) => <span className="font-body text-sm text-stone-700">{a.outdoor}</span>,
        },
        {
            label: t({ de: "Carport", en: "Carport" }),
            render: () => <Check size={16} className="text-brand-500" />,
        },
        {
            label: t({ de: "Kellerraum", en: "Storage room" }),
            render: (a) => (a.cellar ? <Check size={16} className="text-brand-500" /> : <Minus size={16} className="text-stone-300" />),
        },
        {
            label: t({ de: "Fahrradständer", en: "Bike racks" }),
            render: () => <Check size={16} className="text-brand-500" />,
        },
        {
            label: t({ de: "Barrierefrei", en: "Barrier-free" }),
            render: (a) => (a.accessible ? <Check size={16} className="text-brand-500" /> : <Minus size={16} className="text-stone-300" />),
        },
        {
            label: t({ de: "Wärmepumpe", en: "Heat pump" }),
            render: () => <Check size={16} className="text-brand-500" />,
        },
        {
            label: t({ de: "PV 2 kW + Speicher", en: "PV 2 kW + storage" }),
            render: () => <Check size={16} className="text-brand-500" />,
        },
        {
            label: t({ de: "Wallbox optional", en: "Wallbox optional" }),
            render: () => <Check size={16} className="text-brand-500" />,
        },
        {
            label: t({ de: "Kaufpreis ab", en: "Price from" }),
            render: (a) => <span className="font-heading text-lg text-brand-600">{a.priceLabel}</span>,
            highlight: true,
        },
        {
            label: t({ de: "Stellplatz", en: "Parking" }),
            render: (a) => <span className="font-body text-sm text-stone-700">{fmtPrice(a.parkingPrice)}</span>,
        },
        {
            label: t({ de: "Gesamt ca. (inkl. NK)", en: "Total approx. (incl. costs)" }),
            render: (a) => (
                <span className="font-heading text-lg text-ink">
                    {fmtPrice(Math.round((a.priceValue + a.parkingPrice) * 1.075))}
                </span>
            ),
            highlight: true,
        },
    ];

    return (
        <section
            id="vergleich"
            className="bg-white py-24 sm:py-32"
            data-testid="apartments-compare-section"
        >
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-10 mb-12">
                    <div className="lg:col-span-7">
                        <span className="overline">{t({ de: "03 / 12 — Direkter Vergleich", en: "03 / 12 — Side by side" })}</span>
                        <h2 className="h-section mt-4 text-balance">
                            {t({ de: "Welche Wohnung", en: "Which apartment" })}
                            <span className="italic text-brand-500"> {t({ de: "passt zu Ihnen?", en: "is right for you?" })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="body-md">
                            {t({
                                de: "Alle vier Wohnungen auf einen Blick – damit Sie die für Sie richtige schneller finden. Klicken Sie auf eine Spalte, um den Detail-Dialog mit Anfrage-Formular zu öffnen.",
                                en: "All four apartments at a glance – so you can find the right one faster. Click a column to open the detail dialog with the inquiry form.",
                            })}
                        </p>
                    </div>
                </div>

                <div className="overflow-x-auto elegant-scroll -mx-6 sm:mx-0">
                    <div className="min-w-[860px] sm:min-w-0">
                        <table
                            className="w-full bg-cream border border-stone-200 border-collapse"
                            data-testid="compare-table"
                        >
                            <thead>
                                <tr className="bg-white">
                                    <th className="w-44 px-5 py-5 text-left align-bottom border-b border-stone-200">
                                        <span className="overline">Eigenschaft</span>
                                    </th>
                                    {APARTMENTS.map((a) => {
                                        const sold = statusMap[a.id] === "verkauft";
                                        return (
                                            <th
                                                key={a.id}
                                                className={`px-4 sm:px-5 py-5 text-left align-bottom border-b border-l border-stone-200 group ${
                                                    sold ? "opacity-70" : ""
                                                }`}
                                                data-testid={`compare-col-${a.id}`}
                                            >
                                                <div className="aspect-[16/10] -mx-4 sm:-mx-5 -mt-5 mb-4 overflow-hidden bg-stone-100">
                                                    <img src={a.image} alt={a.name} className={`w-full h-full object-cover ${sold ? "grayscale-[40%]" : ""}`} />
                                                </div>
                                                <div className="overline text-stone-400 mb-1">{a.shortName}</div>
                                                <h3 className="font-heading text-lg text-ink leading-tight">
                                                    {a.name.split(" · ")[0]}
                                                </h3>
                                                <div className="font-body text-xs text-stone-500 mt-1">
                                                    {a.tagline}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr
                                        key={r.label}
                                        className={`${
                                            i % 2 === 0 ? "bg-cream" : "bg-white"
                                        } ${r.highlight ? "bg-clay-50" : ""}`}
                                        data-testid={`compare-row-${r.label}`}
                                    >
                                        <td className="px-5 py-4 font-body text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold border-b border-stone-200">
                                            {r.label}
                                        </td>
                                        {APARTMENTS.map((a) => {
                                            const sold = statusMap[a.id] === "verkauft";
                                            return (
                                                <td
                                                    key={a.id}
                                                    className={`px-4 sm:px-5 py-4 align-middle border-b border-l border-stone-200 ${
                                                        sold ? "opacity-60" : ""
                                                    }`}
                                                >
                                                    {r.render(a)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                                {/* Action row */}
                                <tr className="bg-white">
                                    <td className="px-5 py-5 font-body text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">
                                        {t({ de: "Anfrage", en: "Inquire" })}
                                    </td>
                                    {APARTMENTS.map((a) => {
                                        const sold = statusMap[a.id] === "verkauft";
                                        return (
                                            <td key={a.id} className="px-4 sm:px-5 py-5 border-l border-stone-200">
                                                <button
                                                    onClick={() => setSelected(a)}
                                                    className={`w-full inline-flex items-center justify-between gap-2 px-4 py-2.5 transition-colors text-sm font-body font-medium ${
                                                        sold
                                                            ? "border border-stone-300 text-stone-500 cursor-default"
                                                            : "bg-ink text-white hover:bg-brand-500"
                                                    }`}
                                                    data-testid={`compare-cta-${a.id}`}
                                                    disabled={sold}
                                                >
                                                    {sold ? t({ de: "Verkauft", en: "Sold" }) : t({ de: "Details", en: "Details" })}
                                                    {!sold && <ArrowUpRight size={14} />}
                                                </button>
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <p className="font-body text-xs text-stone-500 mt-4">
                    {t({
                        de: "Tipp: Auf dem Smartphone können Sie die Tabelle horizontal wischen. Nebenkosten = 6 % Grunderwerbsteuer Hessen + ca. 1,5 % Notar & Grundbuch (Direktverkauf vom Bauträger – maklerfrei).",
                        en: "Tip: on mobile you can swipe the table horizontally. Closing costs = 6 % real-estate transfer tax in Hesse + approx. 1.5 % notary & land register (direct sale from the developer – brokerage-free).",
                    })}
                </p>
            </div>

            <ApartmentDetailDialog
                apartment={selected}
                status={selected ? statusMap[selected.id] : null}
                open={Boolean(selected)}
                onOpenChange={(o) => !o && setSelected(null)}
            />
        </section>
    );
};
